import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { JsonDateParser } from "../../../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { UserClaim } from "../../../../../../../../../Wrap/Constants/Enums/UserClaim";
import { ClientAccount } from "../../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { TransferModel } from "../../../../../../../../../Wrap/Models/Models/Transfer/TransferModel";
import { checkPermission } from "../../../../../../../../Shared/Permission/PermissionHelper";
import { Table } from "../../../../../../../../Shared/Table/Table";
import { TransferStatusMapper, TransferTypeMapper } from "../../Constant/Content";
import { SubAccountType } from "../../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { TransfersDonutChart } from "../TransfersDonutChart";
import { Money } from "../../../../../../../../../Wrap/Core/DataTypes/Money";

interface ITransfers {
    completedTransferIn: TransferModel[];
    headaccount: ClientAccount;
    subAccountType?: SubAccountType;
}

class IChartData {
    label: string;
    value: Money;
    allocation: number;
    color: string;
}

const renderCompleteTransfers = (data: TransferModel[], claccountId: string, subAccountType: SubAccountType) => {
    // calculate Total amount
    const reducer = (accumulator, currentvalue) => {
        return accumulator + currentvalue.Amount;
    };
    const totalAmount = data.reduce(reducer, 0);

    const isNpr = subAccountType === SubAccountType.Npr || subAccountType === SubAccountType.JuniorSipp;

    const transfersCol = [
        { title: "Ceding providers", key: "Provider" },
        {
            title: "Type",
            key: "ArrangementType",
            render: (data) => {
                return <span style={{ color: "green" }}>{data}</span>;
            },
        },
        {
            title: "Status",
            key: "Status",
            render: (data) => {
                return <span style={{ color: "green" }}>{TransferStatusMapper[data]}</span>;
            },
        },
        {
            title: "Completed date",
            key: "DateCompleted",
            render: (data) => {
                return JsonDateParser.getFormatDate(data, "dd/MM/yyyy");
            },
        },
        {
            title: isNpr ? "Value" : "Amount",
            key: "Amount",
            tdCssWrapper: "cell-right",
            thCssWrapper: "cell-right",
            render: (data) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(data, CurrencyCode.GBP, true, 2);
            },
        },
    ];

    const filteredTransfersCol = isNpr
        ? transfersCol : transfersCol.filter((col) => col.key !== "ArrangementType");

    const getChartData = () => {
        if (!data?.length) {
            return [];
        }

        const totalAmount = data.reduce((sum, transfer) => sum += transfer.Amount, 0)
        let groupedTransferData: Array<TransferModel> = [];

        if (isNpr) {
            data.forEach(transfer => {
                if (!groupedTransferData.find(x => x.ArrangementType == transfer.ArrangementType)) {
                    groupedTransferData.push({ ArrangementType: transfer.ArrangementType, Amount: 0});
                }
                groupedTransferData.find(x => x.ArrangementType == transfer.ArrangementType).Amount += transfer.Amount;
            }, {});
        } else {
            data.forEach(transfer => {
                if (!groupedTransferData.find(x => x.TransferType == transfer.TransferType)) {
                    groupedTransferData.push({ TransferType: transfer.TransferType, Amount: 0});
                }
                groupedTransferData.find(x => x.TransferType == transfer.TransferType).Amount += transfer.Amount;
            }, {});
        }

        const colors = generateColor(groupedTransferData.length);
        let chartData: Array<IChartData> = [];
        groupedTransferData.forEach((transfer, index) => {
            const displayName = isNpr ? transfer.ArrangementType : TransferTypeMapper[transfer.TransferType];
            const amount: Money = {
                Amount: transfer.Amount,
                Currency: CurrencyCode.GBP
            }
            let chart: IChartData = {
                label: displayName,
                value: amount,
                allocation: Number((transfer.Amount / totalAmount * 100).toFixed(2)),
                color: colors[index],
            };
            chartData.push(chart);
        });
        return chartData;
    };

    const generateColor = (num: number) => {
        const defaultColors = ["#2caffe", "#544fc5", "#00e272", "#fe6a35", "#6b8abc"
            , "#d568fb", "#2ee0ca", "#fa4b42", "#feb56a", "#91e8e1"];
        let colorSet = new Set<string>();

        while (colorSet.size < num) {
            defaultColors.forEach(color => {
                if (colorSet.size < num) {
                    colorSet.add(color);
                }
            });
        }

        return Array.from(colorSet);
    };

    return (
        <>
            {data?.length && (
                <>
                    <div className="panel-subtle panel-scrollable mb-50">
                        <TransfersDonutChart data={getChartData()} />
                    </div>
                </>
            )}
            <Table
                cssWrapper="table-subtle table-first-last-cell-padding-reset mb-10"
                data={data}
                col={filteredTransfersCol}
                TotalBody={[
                    { data: "Total", colSpan: filteredTransfersCol.length - 1 },
                    {
                        data: MoneyDisplayHelper.FromatMoneyDisplay(totalAmount, CurrencyCode.GBP, true, 2),
                        colSpan: 1,
                        tfTdCssWrapper: "cell-right",
                    },
                ]}
            />
            {checkPermission(UserClaim.AdviserPortalDluReregistrationTransfersCanView) && (
                <a
                    href={`/clients/tracking/reregistration-transfers.aspx?claccountid=${claccountId}&txtStatus=Completed`}
                    className="hyperlink"
                >
                    View completed transfers
                </a>
            )}
        </>
    );
};

export const Transfers = (props: ITransfers) => {
    return (
        <>
            <h3 className="mb-5">Transfers</h3>
            {props.completedTransferIn?.length > 0 ? (
                <div className="panel-subtle mb-26 pt-2 pb-32">
                    {renderCompleteTransfers(props.completedTransferIn, props.headaccount?.HeadClAccountId, props.subAccountType)}
                </div>
            ) : (
                <div className="panel-subtle mb-35">No transfer(s) currently set up for this client</div>
            )}
        </>
    );
};
